import React from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, Paper, List, ListItem, ListItemText, Chip } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import TrendingUpIcon from '@material-ui/icons/TrendingUp';

const useStyles = makeStyles((theme) => ({
  root: {
    marginBottom: theme.spacing(2),
    padding: theme.spacing(3)
  },
  title: {
    marginBottom: theme.spacing(2),
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  list: {
    padding: 0
  },
  recommendationItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1, 0)
  },
  recommendationText: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(1)
  },
  recommendationIcon: {
    color: theme.palette.success.main,
    marginTop: theme.spacing(0.5)
  },
  priorityChip: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText
  },
  metrics: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: theme.spacing(2)
  },
  metric: {
    padding: theme.spacing(1),
    backgroundColor: theme.palette.background.default,
    borderRadius: theme.shape.borderRadius
  },
  metricLabel: {
    fontSize: '0.875rem',
    color: theme.palette.text.secondary
  },
  metricValue: {
    fontSize: '1.25rem',
    fontWeight: 500
  }
}));

const RecommendationsSection = ({ content, priority, metrics }) => {
  const classes = useStyles();

  if (!content || content.length === 0) {
    return null;
  }

  const getPriorityColor = (priority) => {
    switch (priority?.toLowerCase()) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Paper className={classes.root}>
      <Typography variant="h6" className={classes.title}>
        <TrendingUpIcon />
        Recommendations
      </Typography>
      <Box className={classes.content}>
        {priority && (
          <Chip
            label={`${priority.toUpperCase()} Priority`}
            color={getPriorityColor(priority)}
            size="small"
            className={classes.priorityChip}
          />
        )}

        {Object.keys(metrics).length > 0 && (
          <Box className={classes.metrics}>
            {Object.entries(metrics).map(([key, value]) => (
              <Box key={key} className={classes.metric}>
                <Typography className={classes.metricLabel}>
                  {key.charAt(0).toUpperCase() + key.slice(1)}
                </Typography>
                <Typography className={classes.metricValue}>
                  {value}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        <List className={classes.list}>
          {content.map((recommendation, index) => (
            <ListItem key={index} className={classes.recommendationItem}>
              <Box className={classes.recommendationText}>
                <TrendingUpIcon className={classes.recommendationIcon} />
                <ListItemText primary={recommendation} />
              </Box>
            </ListItem>
          ))}
        </List>
      </Box>
    </Paper>
  );
};

RecommendationsSection.propTypes = {
  content: PropTypes.arrayOf(PropTypes.string).isRequired,
  priority: PropTypes.string,
  metrics: PropTypes.object
};

RecommendationsSection.defaultProps = {
  priority: null,
  metrics: {}
};

export default RecommendationsSection; 